import React, { useState, useEffect, useCallback } from "react";
import { View, StyleSheet } from "react-native";
import ChatHeader from "./ChatHeader";
import MessagesContainer from "./MessagesContainer";
import ChatEmptyState from "./ChatEmptyState";
import {
  useGetCompanionChatByIdQuery,
  useGetAllCompanionChatsForUserQuery,
  AttachmentInput,
} from "../../graphql/generated/graphql";
import useCompanionLlmStream from "../../hooks/useCompanionLlmStream";
import { v4 as uuidv4 } from "uuid";
import { WorkspaceHeader } from "../../components/chatNewInterface/interfaceComponents/workspaceHeader/WorkspaceHeader";
import { ThreadSelector } from "./ThreadSelector";
import { useWorkspaceThreads } from "../../contexts/WorkspaceThreadsContext";
import CustomText from "../../components/common/general/CustomText/CustomText";
import ChatInputController from "./ChatInputController";
const GENERAL_WORKSPACE_ID = "general_chats";

interface ChatWindowContainerProps {
  chatId: string | null;
  onChatIdChange?: (chatId: string) => void;
  isLoadingChatId?: boolean;
}

interface Message {
  sender: string;
  content: string;
  created?: string;
  id: string;
}

const ChatWindowContainer = ({ chatId, onChatIdChange, isLoadingChatId = false }: ChatWindowContainerProps) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentStreamingMessageId, setCurrentStreamingMessageId] = useState<string | null>(null);
  const [resetAutoScroll, setResetAutoScroll] = useState(0);
  const { activeWorkspaceId } = useWorkspaceThreads();

  const { data, loading } = useGetCompanionChatByIdQuery({
    skip: !chatId,
    fetchPolicy: "network-only",
    variables: {
      id: chatId!,
    },
  });

  const { data: workspaceData } = useGetAllCompanionChatsForUserQuery({});

  const { startStream, cancelStream, isStreaming } = useCompanionLlmStream();

  const chatWorkspaces = data?.getCompanionChatById?.workspacesHasChat ?? [];
  const courseWorkspaces = workspaceData?.getAllCompanionChatsForUser?.courseWorkspaces ?? [];
  const supportWorkspaces = workspaceData?.getAllCompanionChatsForUser?.supportWorkspaces ?? [];

  const resolvedWorkspaceId =
    activeWorkspaceId && chatWorkspaces.some((w) => w.id === activeWorkspaceId)
      ? activeWorkspaceId
      : (chatWorkspaces[0]?.id ?? activeWorkspaceId ?? GENERAL_WORKSPACE_ID);

  const currentWorkspaceType: "general" | "course" | "support" = (() => {
    if (resolvedWorkspaceId === GENERAL_WORKSPACE_ID) {
      return "general";
    }

    // First try to get type from chat data (when chatId exists)
    const workspaceFromChat = chatWorkspaces.find((w) => w.id === resolvedWorkspaceId);
    if (workspaceFromChat?.type) {
      return workspaceFromChat.type === "course" ? "course" : "support";
    }

    // Fallback to workspace lists (for new chats during streaming)
    if (courseWorkspaces.some((w) => w.id === resolvedWorkspaceId)) {
      return "course";
    }
    if (supportWorkspaces.some((w) => w.id === resolvedWorkspaceId)) {
      return "support";
    }

    return "general";
  })();

  const workspaceName = (() => {
    if (resolvedWorkspaceId === GENERAL_WORKSPACE_ID) {
      return "General Chat";
    }
    const workspace =
      courseWorkspaces.find((w) => w.id === resolvedWorkspaceId) ||
      supportWorkspaces.find((w) => w.id === resolvedWorkspaceId);

    return workspace?.name;
  })();

  useEffect(() => {
    if (!chatId) {
      setMessages([]);
      return;
    }
    const conversationMessages: Message[] = (data?.getCompanionChatById?.conversation ?? [])
      .map((msg) => {
        const content = msg.content || "";
        const sender = msg.sender || "ai";

        return {
          id: msg.id,
          sender,
          content,
          created: msg.created || undefined,
        };
      })
      .reverse();

    setMessages(conversationMessages);
  }, [data, chatId]);

  const appendChunkToMessage = useCallback(
    ({ text, messageId }: { text: string; messageId?: string }) => {
      if (!text) {
        return;
      }

      if (messageId && !currentStreamingMessageId) {
        setCurrentStreamingMessageId(messageId);
      }

      const targetId = messageId || currentStreamingMessageId;

      if (!targetId) {
        return;
      }

      setMessages((prev) => {
        let found = false;
        const updated = prev.map((message) => {
          if (message.id !== targetId) {
            return message;
          }
          found = true;
          return {
            ...message,
            content: `${message.content}${text}`,
          };
        });

        if (!found) {
          return [
            ...prev,
            {
              id: targetId,
              sender: "ai",
              content: text,
            },
          ];
        }

        return updated;
      });
    },
    [currentStreamingMessageId]
  );

  const handleStreamComplete = useCallback(
    (payload?: { event?: { chatId?: string; messageId?: string }; text: string }) => {
      const finalText = payload?.text;
      const completionEvent = payload?.event;

      if (completionEvent?.chatId && !chatId && onChatIdChange) {
        onChatIdChange(completionEvent.chatId);
      }

      if (finalText) {
        const finalId = completionEvent?.messageId;
        const streamingId = currentStreamingMessageId;
        if (finalId && streamingId) {
          setMessages((prev) =>
            prev.map((message) => {
              if (message.id === streamingId) {
                return {
                  ...message,
                  id: finalId,
                  sender: "ai",
                  content: finalText,
                };
              }
              return message;
            })
          );
        }
        setCurrentStreamingMessageId(null);
      } else {
        setCurrentStreamingMessageId(null);
      }
    },
    [chatId, onChatIdChange, currentStreamingMessageId]
  );

  const handleStreamError = useCallback(
    ({ error }: { error: Error }) => {
      console.error("[ChatWindowContainer] Stream error", { message: error.message });
      const targetId = currentStreamingMessageId;
      if (targetId) {
        setMessages((prev) => prev.filter((message) => message.id !== targetId));
      }
      setCurrentStreamingMessageId(null);
    },
    [currentStreamingMessageId]
  );
  const handleSubmit = useCallback(
    async (message: string, attachment: AttachmentInput | null) => {
      if (!message.trim() || isStreaming) {
        return;
      }

      const trimmedInput = message.trim();

      const userMessage: Message = {
        id: uuidv4(),
        sender: "human",
        content: trimmedInput,
      };

      setMessages((prev) => [...prev, userMessage]);

      setCurrentStreamingMessageId(null);
      setResetAutoScroll((prev) => prev + 1);

      try {
        const workspaceId =
          activeWorkspaceId && activeWorkspaceId !== GENERAL_WORKSPACE_ID ? activeWorkspaceId : undefined;

        await startStream(trimmedInput, {
          chatId: chatId || undefined,
          workspaceId,
          attachment: attachment || undefined,
          onChunk: appendChunkToMessage,
          onComplete: handleStreamComplete,
          onError: handleStreamError,
        });
      } catch (error) {
        console.error("[ChatWindowContainer] Failed to start stream:", error);
        handleStreamError({ error: error as Error });
      }
    },
    [isStreaming, chatId, activeWorkspaceId, startStream, appendChunkToMessage, handleStreamComplete, handleStreamError]
  );

  const isEmptyState = messages.length === 0 && !isLoadingChatId && (!chatId || !loading);

  useEffect(() => {
    return () => {
      cancelStream();
    };
  }, [cancelStream]);

  if ((chatId && loading) || isLoadingChatId) {
    return (
      <View style={styles.container}>
        <ChatHeader />
        <View style={styles.content}>
          <View style={styles.messagesArea}>
            <View style={styles.loadingContainer}>{/* Loading indicator */}</View>
          </View>
        </View>
      </View>
    );
  }

  if (isEmptyState) {
    return (
      <ChatEmptyState
        title={workspaceName}
        input={
          <ChatInputController
            onSubmit={handleSubmit}
            disabled={isStreaming}
            placeholder="Type a message..."
            activeWorkspaceType={currentWorkspaceType}
            showSuggestions={false}
          />
        }
      />
    );
  }

  return (
    <View style={styles.container}>
      <WorkspaceHeader
        title={workspaceName}
        middleAction={
          !!chatId && (
            <View style={styles.threadSelector}>
              <ThreadSelector
                workspaceId={resolvedWorkspaceId}
                threadId={chatId}
                threadName={data?.getCompanionChatById?.name || ""}
              />
            </View>
          )
        }
      />
      <View style={styles.content}>
        <View style={styles.messagesArea}>
          <MessagesContainer
            messages={messages}
            isStreaming={isStreaming}
            currentStreamingMessageId={currentStreamingMessageId}
            resetAutoScroll={resetAutoScroll}
          />
          <View style={styles.chatInputWrapper}>
            <ChatInputController
              onSubmit={handleSubmit}
              disabled={isStreaming}
              placeholder="Type a message..."
              activeWorkspaceType={currentWorkspaceType}
            />
            <View style={styles.disclaimerContainer}>
              <CustomText
                useTranslationText={false}
                text={"Axio can make mistakes. Please verify important information."}
                style={styles.disclaimerText}
              />
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    display: "flex",
    flexDirection: "column",
  },
  chatInputWrapper: {
    width: "100%",
    maxWidth: 1152,
    alignSelf: "center",
  },
  content: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    overflow: "hidden",
  },
  messagesArea: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#f9fafbb3",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  threadSelector: {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: [{ translateX: "-50%" }, { translateY: "-50%" }],
  },
  disclaimerContainer: {
    paddingBottom: 8,
    alignItems: "center",
  },
  disclaimerText: {
    fontSize: 11,
    color: "#667085",
  },
});

export default ChatWindowContainer;
